import axios from 'axios'
import React, {useState} from 'react'
import {Alert, Button, Form, Modal, Spinner} from 'react-bootstrap'
import apiRouterItems from '../../../../constants/apiRouterItems'
import useEventLog from '../../../../hooks/useEventLog'
import {EventCategories, EventTypes} from '../../../../constants/eventConstants'
import {JobModel} from '../../../../models/JobModel'
import JobEditModal from './JobEditModal'

type Props = {
  show: boolean
  onHide: (reload?: boolean, createdJobId?: string) => void
  /** When set, the new job is added to this client site's job list on create. */
  clientSiteId?: string
  clientId?: string
}

type HybridJobDescriptionResponse = {
  sourceDescription?: string
  jobName: string
  jobDescription: string
  keyword?: string
  ONetCode?: string
  topCompetencyIds: string[]
}

function renderDescriptionText(text: string) {
  if (!text) {
    return <span className='text-muted'>—</span>
  }
  if (text.includes('<')) {
    return <div dangerouslySetInnerHTML={{__html: text}} />
  }
  return text
}

type JdInputMode = 'manual' | 'file'
type ProgressStatus = 'pending' | 'active' | 'completed' | 'failed'
type ProgressStep = {
  key: 'upload' | 'ai' | 'create'
  label: string
  status: ProgressStatus
  detail?: string
}

const JobAiCreateModal: React.FC<Props> = ({show, onHide, clientSiteId, clientId}) => {
  const [jdInputMode, setJdInputMode] = useState<JdInputMode>('manual')
  const [manualDescription, setManualDescription] = useState('')
  const [jdFile, setJdFile] = useState<File | null>(null)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState('')
  const [createdJobName, setCreatedJobName] = useState('')
  const [createdJobDescription, setCreatedJobDescription] = useState('')
  const [ingestedJobDescription, setIngestedJobDescription] = useState('')
  const [ingestedSourceLabel, setIngestedSourceLabel] = useState('')
  const [createdJobId, setCreatedJobId] = useState('')
  const [createdJob, setCreatedJob] = useState<JobModel | undefined>()
  const [showViewJobModal, setShowViewJobModal] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([])
  const {addEventLog} = useEventLog()

  function initProgress(mode: JdInputMode) {
    setUploadProgress(0)
    if (mode === 'file') {
      setProgressSteps([
        {key: 'upload', label: 'Uploaded job description', status: 'pending'},
        {key: 'ai', label: 'Processing with AI', status: 'pending'},
        {key: 'create', label: 'Creating new job', status: 'pending'},
      ])
    } else {
      setProgressSteps([
        {key: 'upload', label: 'Manual job description received', status: 'pending'},
        {key: 'ai', label: 'Processing with AI', status: 'pending'},
        {key: 'create', label: 'Creating new job', status: 'pending'},
      ])
    }
  }

  function updateStep(
    key: ProgressStep['key'],
    status: ProgressStatus,
    detail: string = ''
  ) {
    setProgressSteps((prev) =>
      prev.map((item) => (item.key === key ? {...item, status, detail} : item))
    )
  }

  function getOverallProgressValue() {
    if (!progressSteps.length) {
      return 0
    }
    let score = 0
    progressSteps.forEach((step) => {
      if (step.status === 'completed') {
        score += 1
      } else if (step.status === 'active') {
        score += 0.5
      }
    })
    return Math.round((score / progressSteps.length) * 100)
  }

  function createAutoJobCode(name: string) {
    const normalizedName = (name || 'JOB')
      .toUpperCase()
      .replace(/[^A-Z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 12)
    const suffix = Date.now().toString().slice(-6)
    return `${normalizedName || 'JOB'}-${suffix}`
  }

  function resetState() {
    setJdInputMode('manual')
    setManualDescription('')
    setJdFile(null)
    setProcessing(false)
    setError('')
    setCreatedJobName('')
    setCreatedJobDescription('')
    setIngestedJobDescription('')
    setIngestedSourceLabel('')
    setCreatedJobId('')
    setCreatedJob(undefined)
    setShowViewJobModal(false)
    setUploadProgress(0)
    setProgressSteps([])
  }

  function closeModal() {
    const shouldReload = !!createdJobName
    const jobId = createdJobId
    resetState()
    onHide(shouldReload, jobId)
  }

  async function handleCreate() {
    setProcessing(true)
    setError('')
    initProgress(jdInputMode)
    try {
      const formData = new FormData()
      if (jdInputMode === 'manual') {
        updateStep('upload', 'active', 'Manual text prepared')
        formData.append('manualDescription', manualDescription.trim())
        updateStep('upload', 'completed', 'Manual text received')
      } else if (jdFile) {
        updateStep('upload', 'active', `Uploading ${jdFile.name}`)
        formData.append('file', jdFile)
      }

      updateStep('ai', 'active', 'Sending job description to AI engine')
      const {data: hybridData} = await axios.post<HybridJobDescriptionResponse>(
        apiRouterItems.JOBS_HYBRID_DESCRIPTION,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          onUploadProgress: (progressEvent) => {
            if (jdInputMode !== 'file') {
              return
            }
            if (!progressEvent.total) {
              return
            }
            const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            setUploadProgress(percentage)
            updateStep('upload', percentage >= 100 ? 'completed' : 'active', `Upload ${percentage}%`)
          },
        }
      )
      if (jdInputMode === 'file') {
        updateStep('upload', 'completed', 'File uploaded')
      }
      updateStep('ai', 'completed', 'AI generated title, description, and competencies')

      const payload = {
        code: createAutoJobCode(hybridData.jobName),
        ONetCode: hybridData.ONetCode || 'AUTO',
        name: hybridData.jobName || '',
        altTitle: '',
        keyword: hybridData.keyword || '',
        description: hybridData.jobDescription || '',
        topCompetencyIds: hybridData.topCompetencyIds || [],
        ...(clientSiteId ? {clientSiteId} : {}),
        ...(clientId ? {clientId} : {}),
      }
      updateStep('create', 'active', 'Saving generated job to database')
      const {data: createdJob} = await axios.post(apiRouterItems.JOBS, payload)
      updateStep('create', 'completed', 'New job created successfully')
      setIngestedJobDescription(
        hybridData.sourceDescription ||
          (jdInputMode === 'manual' ? manualDescription.trim() : '')
      )
      setIngestedSourceLabel(
        jdInputMode === 'file' && jdFile ? `From file: ${jdFile.name}` : 'Pasted text'
      )
      setCreatedJobName(payload.name)
      setCreatedJobDescription(payload.description)
      setCreatedJobId(createdJob?._id || '')
      setCreatedJob({
        ...(createdJob || {}),
        _id: createdJob?._id || '',
        code: payload.code,
        ONetCode: payload.ONetCode,
        name: payload.name,
        altTitle: payload.altTitle,
        keyword: payload.keyword,
        description: payload.description,
        topCompetencyIds: payload.topCompetencyIds,
      })

      addEventLog({
        eventType: EventTypes.CREATED,
        eventCategory: EventCategories.JOB,
        description: `Job '${payload.name}' was created by AI JD flow`,
      })
    } catch (err: any) {
      const message = err?.response?.data?.message || 'Failed to create job from JD.'
      setError(message)
      setProgressSteps((prev) => {
        const activeStep = prev.find((item) => item.status === 'active')
        if (activeStep) {
          return prev.map((item) =>
            item.key === activeStep.key ? {...item, status: 'failed', detail: message} : item
          )
        }
        return prev.map((item) =>
          item.key === 'ai' ? {...item, status: 'failed', detail: message} : item
        )
      })
      addEventLog({
        eventType: EventTypes.FAILURE,
        eventCategory: EventCategories.JOB,
        description: `Error: ${typeof err === 'string' ? err : JSON.stringify(err)}
        Description: AI job creation from JD
        Page: Super admin job page
        File: JobAiCreateModal
        Action: axios.post
      `,
      })
    }
    setProcessing(false)
  }

  return (
    <Modal show={show} onHide={() => closeModal()} size='lg'>
      <Modal.Header closeButton>
        <Modal.Title>Create Job From JD (AI)</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant='danger' show={!!error}>
          {error}
        </Alert>
        <Alert variant='success' show={!!createdJobName}>
          <div className='mb-2'>
            Job <b>{createdJobName}</b> was created successfully.
          </div>
          <div className='fw-bold mb-2 mt-3'>Original vs AI-generated</div>
          <div className='row g-3'>
            <div className='col-md-6'>
              <div className='border rounded p-3 bg-light text-dark h-100'>
                <div className='fw-bold mb-1'>Original ingested job description</div>
                {!!ingestedSourceLabel && (
                  <div className='text-muted fs-8 mb-2'>{ingestedSourceLabel}</div>
                )}
                <div
                  className='fs-7'
                  style={{maxHeight: 220, overflowY: 'auto', whiteSpace: 'pre-wrap'}}
                >
                  {renderDescriptionText(ingestedJobDescription)}
                </div>
              </div>
            </div>
            <div className='col-md-6'>
              <div className='border rounded p-3 bg-white text-dark h-100'>
                <div className='fw-bold mb-1'>AI-generated job</div>
                <div className='text-muted fs-8 mb-2'>Title and description</div>
                <div className='fw-semibold mb-2'>{createdJobName}</div>
                <div
                  className='fs-7'
                  style={{maxHeight: 220, overflowY: 'auto', whiteSpace: 'pre-wrap'}}
                >
                  {renderDescriptionText(createdJobDescription)}
                </div>
              </div>
            </div>
          </div>
          <div className='d-flex flex-wrap gap-2 mt-3'>
            <Button variant='primary' size='sm' onClick={() => setShowViewJobModal(true)}>
              <i className='fas fa-external-link-alt me-2' />
              View Job
            </Button>
          </div>
          <div className='text-muted fs-7 mt-2 mb-0'>
            Compare the original input with the AI output, use View Job to open full details, or
            close to continue.
          </div>
        </Alert>
        <Form>
          <Form.Group className='mb-4'>
            <Form.Label className='fw-bold'>Job Description Input Mode</Form.Label>
            <div className='d-flex gap-5 mt-2'>
              <Form.Check
                type='radio'
                id='ai-jd-mode-manual'
                name='ai-jd-mode'
                label='Manual text'
                checked={jdInputMode === 'manual'}
                onChange={() => {
                  setJdInputMode('manual')
                  setJdFile(null)
                }}
                disabled={processing || !!createdJobName}
              />
              <Form.Check
                type='radio'
                id='ai-jd-mode-file'
                name='ai-jd-mode'
                label='Upload file'
                checked={jdInputMode === 'file'}
                onChange={() => {
                  setJdInputMode('file')
                  setManualDescription('')
                }}
                disabled={processing || !!createdJobName}
              />
            </div>
          </Form.Group>
          <Form.Group className='mb-4'>
            <Form.Control
              as='textarea'
              rows={6}
              placeholder='Paste job description text here...'
              value={manualDescription}
              disabled={jdInputMode !== 'manual' || processing || !!createdJobName}
              onChange={(e) => setManualDescription(e.target.value)}
            />
          </Form.Group>
          <Form.Group className='mb-2'>
            <Form.Control
              type='file'
              accept='.pdf,.txt,.docx'
              disabled={jdInputMode !== 'file' || processing || !!createdJobName}
              onChange={(e) => {
                const input = e.target as HTMLInputElement
                setJdFile(input.files?.[0] || null)
              }}
            />
          </Form.Group>
          <div className='text-muted fs-7'>
            AI will create a new job title, description, and top 10 competencies.
          </div>
          {!!progressSteps.length && (
            <div className='mt-4 border rounded p-3 bg-light'>
              <div className='fw-bold mb-2'>Real-time progress</div>
              <div className='mb-3'>
                <div className='d-flex justify-content-between fs-7 text-muted mb-1'>
                  <span>Overall progress</span>
                  <span>{getOverallProgressValue()}%</span>
                </div>
                <div className='progress h-8px'>
                  <div
                    className='progress-bar bg-primary'
                    role='progressbar'
                    style={{width: `${getOverallProgressValue()}%`}}
                    aria-valuenow={getOverallProgressValue()}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
              </div>
              {jdInputMode === 'file' && (
                <div className='mb-3'>
                  <div className='d-flex justify-content-between fs-7 text-muted mb-1'>
                    <span>Upload progress</span>
                    <span>{uploadProgress}%</span>
                  </div>
                  <div className='progress h-8px'>
                    <div
                      className='progress-bar bg-info'
                      role='progressbar'
                      style={{width: `${uploadProgress}%`}}
                      aria-valuenow={uploadProgress}
                      aria-valuemin={0}
                      aria-valuemax={100}
                    />
                  </div>
                </div>
              )}
              <div className='d-flex flex-column gap-2'>
                {progressSteps.map((step) => (
                  <div key={step.key} className='d-flex align-items-start'>
                    <div className='me-2 mt-1'>
                      {step.status === 'completed' && <i className='fa fa-check-circle text-success' />}
                      {step.status === 'active' && <Spinner animation='border' size='sm' />}
                      {step.status === 'failed' && <i className='fa fa-times-circle text-danger' />}
                      {step.status === 'pending' && <i className='fa fa-circle text-muted fs-10' />}
                    </div>
                    <div>
                      <div className='fw-semibold'>{step.label}</div>
                      {!!step.detail && <div className='fs-7 text-muted'>{step.detail}</div>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Form>
      </Modal.Body>
      <JobEditModal
        show={showViewJobModal}
        job={createdJob}
        onHide={(reload?: boolean) => {
          setShowViewJobModal(false)
          if (reload && createdJob?._id) {
            axios
              .get<JobModel>(apiRouterItems.JOBS + createdJob._id)
              .then(({data}) => {
                setCreatedJob(data)
                setCreatedJobName(data.name || '')
                setCreatedJobDescription(data.description || '')
              })
              .catch(() => {})
          }
        }}
      />
      <Modal.Footer>
        {!!createdJobName && (
          <Button
            variant='outline-primary'
            className='me-auto'
            onClick={() => setShowViewJobModal(true)}
            disabled={processing}
          >
            View Job
          </Button>
        )}
        <Button
          variant='secondary'
          onClick={() => {
            closeModal()
          }}
          disabled={processing}
        >
          {createdJobName ? 'Close' : 'Cancel'}
        </Button>
        <Button
          variant='primary'
          onClick={() => handleCreate()}
          disabled={
            processing ||
            !!createdJobName ||
            (jdInputMode === 'manual' && !manualDescription.trim()) ||
            (jdInputMode === 'file' && !jdFile)
          }
        >
          {processing && <Spinner animation='border' size='sm' className='me-2' />}
          Create Job with AI
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

JobAiCreateModal.defaultProps = {
  show: false,
  onHide: () => {},
}

export default JobAiCreateModal
